import { useState, useEffect } from "react";
import Icon from "./Icon";
import { settingService } from "../../services/settingService";

export default function LegalDocumentModal({ isOpen, onClose, initialTab = "terms" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState({
    terms: "",
    kvkk: "",
    cookies: "",
    agreement: "",
  });

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      loadDocs();
    }
  }, [isOpen, initialTab]);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await settingService.getLegalDocuments();
      if (res.data?.data) {
        setDocs(res.data.data);
      }
    } catch (err) {
      console.error("Yasal metinler yüklenemedi:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: "terms", label: "Kullanım Koşulları", icon: "gavel" },
    { id: "kvkk", label: "KVKK Aydınlatma Metni", icon: "shield" },
    { id: "cookies", label: "Çerez Politikası", icon: "cookie" },
    { id: "agreement", label: "Mesafeli Hizmet Sözleşmesi", icon: "description" },
  ];

  const renderContent = (text) => {
    if (!text) {
      return <p className="text-on-surface-variant italic text-sm">Metin henüz eklenmemiş.</p>;
    }

    return text.split("\n\n").map((paragraph, pIdx) => {
      const lines = paragraph.trim().split("\n");
      return (
        <div key={pIdx} className="mb-4 space-y-1">
          {lines.map((line, lIdx) => {
            if (line.startsWith("# ")) {
              return (
                <h1 key={lIdx} className="text-xl font-bold text-on-surface pb-2 border-b border-outline-variant/40 mb-3">
                  {line.replace("# ", "")}
                </h1>
              );
            }
            if (line.startsWith("## ")) {
              return (
                <h2 key={lIdx} className="text-base font-bold text-on-surface mt-4 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary inline-block"></span>
                  {line.replace("## ", "")}
                </h2>
              );
            }
            if (line.startsWith("- ")) {
              return (
                <li key={lIdx} className="ml-4 text-sm text-on-surface-variant list-disc">
                  {line.replace("- ", "")}
                </li>
              );
            }
            return (
              <p key={lIdx} className="text-sm text-on-surface-variant leading-relaxed">
                {line}
              </p>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[85vh] shadow-modal border border-outline-variant/40 flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-outline-variant/30 flex items-center justify-between bg-surface-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icon name="verified_user" className="!text-[22px]" filled />
            </div>
            <div>
              <h2 className="text-lg font-bold text-on-surface">Yasal Metinler ve Politikalar</h2>
              <p className="text-xs text-on-surface-variant">SalonVIP hizmet ve gizlilik standartları</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-surface-container-high hover:bg-outline-variant/30 text-on-surface-variant flex items-center justify-center transition"
          >
            <Icon name="close" className="!text-[20px]" />
          </button>
        </div>

        {/* Modal Tabs */}
        <div className="flex border-b border-outline-variant/30 bg-surface-subtle overflow-x-auto px-4 pt-2 no-scrollbar">
          {tabs.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-xs font-bold rounded-t-xl transition whitespace-nowrap border-b-2 ${
                  active
                    ? "border-primary bg-white text-primary shadow-sm"
                    : "border-transparent text-on-surface-variant hover:text-on-surface hover:bg-white/50"
                }`}
              >
                <Icon name={t.icon} className="!text-[16px]" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 bg-white">
          {loading ? (
            <div className="py-12 text-center text-on-surface-variant text-sm flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span>Metin yükleniyor...</span>
            </div>
          ) : (
            <div className="prose max-w-none">
              {renderContent(docs[activeTab])}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-outline-variant/30 bg-surface-card flex items-center justify-between">
          <span className="text-xs text-on-surface-variant flex items-center gap-1">
            <Icon name="lock" className="!text-[14px]" />
            SalonVIP Güvenlik ve Gizlilik Şartları
          </span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-xl hover:bg-primary-hover transition shadow-sm"
          >
            Anladım & Kapat
          </button>
        </div>
      </div>
    </div>
  );
}
